import { useQuery } from "react-query";
import { axiosAuth, axiosNoAuth } from "./axiosConfig";
/**
 * Fetch connector devices.
 * @param {function} successFunc
 * @param {function} errorLogger
 * @returns {object} React Query result
 */

export const useConnectorDevices = (successFunc, errorLogger) => {
  return useQuery(
    "connectorDevices",
    async () => {
      let response = {};
      try {
        response = await axiosNoAuth.get(
          `${process.env.REACT_APP_CONNECTOR_ROUTE}/scan-devices`
        );
      } catch (err) {
        response.status = 400;
        response.message = err.message || "Error scanning devices.";
      }
      // Update the data array to be a list of the serials of the devices returned
      if (response && response.data && response.data.devices) {
        // the deviceId = "serial" of the device
        response.devices = response.data.devices.map((device) => device.serial);
      }
      return response;
    },
    {
      onSuccess: successFunc,
      onError: errorLogger,
    }
  );
};
/**
 * Fetch server devices by IDs.
 * @param {object} data
 * @param {function} successFunc
 * @param {function} errorLogger
 * @returns {object} React Query result
 */

export const useServerDevices = (data, successFunc, errorLogger) => {
  return useQuery(
    ["serverDevices", data],
    async () => {
      if (!data || !data.devices || data.devices.length === 0) return [];
      const ids = data.devices;
      const devices = [];
      await Promise.all(
        ids.map(async (id) => {
          try {
            const result = await axiosAuth.get(`/devices/${id}`);
            devices.push(result.data);
          } catch (err) {
            console.error(
              `Could not retrieve device of id (serial) ${id} from the server.`,
              err
            );
          }
        })
      );
      return devices;
    },
    {
      onSuccess: successFunc,
      onError: errorLogger,
    }
  );
};
/**
 * Calibrate connector device.
 * @param {string} id
 * @param {function} successFunc
 * @param {function} errorLogger
 * @returns {object} React Query result
 */

export const useConnectorCalibrate = (id, successFunc, errorLogger) => {
  return useQuery(
    ["connectorCalibrate", id],
    async () => {
      let response = {};
      try {
        response = await axiosNoAuth.post(
          `${process.env.REACT_APP_CONNECTOR_ROUTE}/calibrate-device`,
          {
            deviceSerial: id,
            scanType: "trace",
          }
        );
      } catch (err) {
        response.status = 400;
        response.message = err.message || "Error calibrating device.";
      }
      return response;
    },
    {
      enabled: false,
      onSuccess: successFunc,
      onError: errorLogger,
    }
  );
};
/**
 * Calibrate server device.
 * @param {string} id
 * @param {string} date
 * @param {function} successFunc
 * @param {function} errorLogger
 * @returns {object} React Query result
 */

export const useServerCalibrate = (id, date, successFunc, errorLogger) => {
  return useQuery(
    ["serverCalibrate", id],
    async () => {
      let response = {};
      try {
        response = await axiosAuth.patch(`/devices/${id}`, {
          lastCalibration: date,
        });
      } catch (err) {
        console.error("Error saving calibration date to server.", err);
        response.status = 400;
        response.message = "Error saving calibration date to server.";
      }
      return response;
    },
    {
      enabled: false,
      onSuccess: successFunc,
      onError: errorLogger,
    }
  );
};
/**
 * Get measurement trace from connector device.
 * @param {string} deviceSerial
 * @param {string} scanType
 * @param {function} errorLogger
 * @returns {object} React Query result
 */

export const useGetMeasurementTrace = (deviceSerial, scanType, errorLogger) => {
  return useQuery(
    "getMeasurementTrace",
    async () => {
      try {
        const { data } = await axiosNoAuth.post(
          `${process.env.REACT_APP_CONNECTOR_ROUTE}/get-trace-measurement`,
          {
            deviceSerial,
            scanType,
          }
        );
        return data;
      } catch (err) {
        console.error("Error getting measurement trace:", err);
        throw err;
      }
    },
    {
      enabled: false,
      retry: false,
      onError: errorLogger,
    }
  );
};
/**
 * Get bulk measurement from connector device.
 * @param {string} deviceSerial
 * @param {string} scanType
 * @param {function} errorLogger
 * @returns {object} React Query result
 */

export const useGetMeasurementBulk = (deviceSerial, scanType, errorLogger) => {
  return useQuery(
    "getMeasurementBulk",
    async () => {
      try {
        const { data } = await axiosNoAuth.post(
          `${process.env.REACT_APP_CONNECTOR_ROUTE}/get-bulk-measurement`,
          {
            deviceSerial,
            scanType,
          }
        );
        return data;
      } catch (err) {
        console.error("Error getting bulk measurement:", err);
        throw err;
      }
    },
    {
      enabled: false,
      retry: false,
      onError: errorLogger,
    }
  );
};
